import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Navigate, Route, Routes } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import Navigation from "./components/Navigation";
import Dashboard from "./pages/Dashboard";
import Backtesting from "./pages/Backtesting";
import CompleteBacktesting from "./pages/CompleteBacktesting";
import DayAheadBacktesting from "./pages/DayAheadBacktesting";
import LiveTesting from "./pages/LiveTesting";
import LiveTrading from "./pages/LiveTrading";
import Health from "./pages/Health";

const GLOBAL_DARK_MODE_COOKIE = "site_dark_mode";

const getCookieValue = (name) => {
  if (typeof document === "undefined") {
    return null;
  }

  const prefix = `${name}=`;
  const cookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith(prefix));

  return cookie ? decodeURIComponent(cookie.slice(prefix.length)) : null;
};

const setCookieValue = (name, value, maxAgeSeconds = 60 * 60 * 24 * 365) => {
  if (typeof document === "undefined") {
    return;
  }

  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
};

const signOutRedirect = () => {
  const clientId =
    process.env.REACT_APP_COGNITO_CLIENT_ID || "42m0lftgoms9389ju2q04hmboi";
  const logoutUri =
    process.env.REACT_APP_COGNITO_LOGOUT_URI || `${window.location.origin}/`;
  const cognitoDomain =
    process.env.REACT_APP_COGNITO_DOMAIN ||
    "https://eu-west-1tuyaq7gb3.auth.eu-west-1.amazoncognito.com";

  window.location.href = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodeURIComponent(logoutUri)}`;
};

const App = () => {
  const auth = useAuth();
  const [darkMode, setDarkMode] = useState(
    () => getCookieValue(GLOBAL_DARK_MODE_COOKIE) === "1"
  );

  useEffect(() => {
    setCookieValue(GLOBAL_DARK_MODE_COOKIE, darkMode ? "1" : "0");
    document.body.classList.toggle("global-dark-mode", darkMode);

    return () => {
      document.body.classList.remove("global-dark-mode");
    };
  }, [darkMode]);

  const handleLogout = () => {
    auth.removeUser();
    signOutRedirect();
  };

  if (auth.isLoading) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          darkMode
            ? "bg-slate-950 text-slate-200"
            : "bg-gradient-to-br from-gray-50 to-gray-100"
        }`}
      >
        <div className={darkMode ? "text-slate-300" : "text-gray-600"}>Loading...</div>
      </div>
    );
  }

  if (auth.error) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          darkMode ? "bg-slate-950" : "bg-gradient-to-br from-gray-50 to-gray-100"
        }`}
      >
        <div className="text-center">
          <div className="text-red-600 mb-4">Authentication error: {auth.error.message}</div>
          <button
            onClick={() => auth.signinRedirect()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <div
        className={`min-h-screen flex items-center justify-center ${
          darkMode ? "bg-slate-950" : "bg-gradient-to-br from-gray-50 to-gray-100"
        }`}
      >
        <div
          className={`rounded-2xl shadow-xl p-8 max-w-md w-full mx-4 ${
            darkMode
              ? "bg-slate-900 text-slate-100 border border-slate-800"
              : "bg-white"
          }`}
        >
          <div className="text-center mb-8">
            <h1 className={`text-3xl font-bold mb-2 ${darkMode ? "text-slate-100" : "text-gray-800"}`}>
              Puzzle XBID
            </h1>
          </div>
          <button
            onClick={() => auth.signinRedirect()}
            className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors"
          >
            Sign in
          </button>
        </div>
      </div>
    );
  }

  const username = auth.user?.profile?.email || auth.user?.profile?.["cognito:username"] || "User";
  // Cognito group claims determine role; only admins may see the Dashboard.
  const userGroups = auth.user?.profile?.["cognito:groups"] || [];
  const isAdmin = userGroups.includes("admin");
  const defaultPath = isAdmin ? "/dashboard" : "/backtesting";

  return (
    <Router>
      <div
        className={`min-h-screen ${
          darkMode ? "bg-slate-950 text-slate-100" : "bg-gradient-to-br from-gray-50 to-gray-100"
        }`}
      >
        <Navigation
          username={username}
          onLogout={handleLogout}
          isAdmin={isAdmin}
          darkMode={darkMode}
          onToggleDarkMode={() => setDarkMode((prev) => !prev)}
        />

        <div className="w-full px-3 py-4">
          <div
            className={`rounded-2xl shadow-xl p-4 ${
              darkMode
                ? "bg-slate-900 text-slate-100 border border-slate-800"
                : "bg-white"
            }`}
          >
            <Routes>
              {isAdmin && (
                <Route path="/dashboard" element={<Dashboard darkMode={darkMode} />} />
              )}
              <Route
                path="/backtesting"
                element={
                  <Backtesting
                    key="v51"
                    darkMode={darkMode}
                    candSetOverride="sk_c51_wf_v5.1.0|ceb_ladder_v4.0.0|size50|sell50|asof2026-07-24"
                    variantLabel="v5.1 (SK + published-CEB, WF CEB)"
                  />
                }
              />
              <Route path="/backtesting-v51" element={<Navigate to="/backtesting" replace />} />
              <Route path="/backtesting-v6" element={<Navigate to="/backtesting-v51" replace />} />
              <Route path="/dayahead" element={<DayAheadBacktesting darkMode={darkMode} />} />
              <Route path="/complete" element={<CompleteBacktesting darkMode={darkMode} />} />
              <Route path="/live" element={<LiveTesting darkMode={darkMode} serviceControl />} />
              <Route path="/live-trading" element={<LiveTrading darkMode={darkMode} />} />
              {isAdmin && (
                <Route path="/health" element={<Health darkMode={darkMode} />} />
              )}
              <Route path="*" element={<Navigate to={defaultPath} replace />} />
            </Routes>
          </div>
        </div>
      </div>
    </Router>
  );
};

export default App;
